import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { theme } from 'antd';
import { CanvasEngine } from '@projectstorm/react-canvas-core';
import { DiagramEngine, NodeModel } from '@projectstorm/react-diagrams';

import { Ports } from '@/comp/MapBuilder/components/Ports';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { getBorderInfo, getFirstPowerSensor, getPowerSensorKw } from '../compressor/CompressorNodeWidget.utils';
import { MetricsNames } from '@/comp/MapBuilder/MetricsNames';
import { Device } from '@/store/Station/constants';
import { isSelectedNodeOnMap, getWidgetFillColor, isValueHighlighted } from '@/comp/MapBuilder/utils/utils';
import { CustomThemeToken } from '@/theme';
import BoosterWidget from '@/comp/common/icons/BoosterWidget';
import BoosterMaintenanceWidget from '@/comp/common/icons/BoosterMaintenanceWidget';
import ConnectivityIcon from '@/comp/MapBuilder/components/ConnectivityIcon/ConnectivityIcon';

import { Entity } from '../nodeWidget/NodeWidget.types';
import { DUMMY_MODEL_IDS } from '../nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';
import { Container } from './BoosterNodeWidget.styles';

const { useToken } = theme;
interface BoosterNodeWidgetProps {
  node: { entity: Entity; device: Device; getEntity; alerts } & NodeModel;
  width: number;
  height: number;
  engine?: CanvasEngine;
  renderedOnMap?: boolean;
  entity?: Entity;
}

export const BoosterNodeWidget = observer(
  ({ engine, node, entity, width, height, renderedOnMap }: BoosterNodeWidgetProps) => {
    const { t } = useTranslation();
    const { stationStore, mapBuilderStore } = useStores();
    const { tvalue, tunit, measurementSystem } = useMeasurement();
    const { tcvalue, tcunit } = useValueByConnectivity();
    const { token } = useToken() as unknown as { token: CustomThemeToken };
    const {
      hp,
      brand,
      modelDisplayName,
      name,
      connectivity,
      inMaintenance,
      deviceModelId,
      messages,
      controlMode,
      deviceId,
    } = entity;
    const onDevices = stationStore.currentStation?.devices?.filter(device =>
      entity.onDevices.includes(device.deviceId),
    );
    const firstPowerSensor = getFirstPowerSensor(entity, onDevices);
    const powerSensorKw = getPowerSensorKw(firstPowerSensor);

    const isSelected = isSelectedNodeOnMap(entity.deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
    const widgetFillColor = getWidgetFillColor(isSelected, token, renderedOnMap);

    const ratedHp = tvalue(hp, 'HP', measurementSystem, 'IMPERIAL');

    const mapViewMetrics = [
      {
        name: MetricsNames.HorsePower,
        value: ratedHp,
        unit: `${tunit('ratedHP')} (${t('Rated')})`,
        highlight: isValueHighlighted(MetricsNames.HorsePower),
      },
      {
        name: MetricsNames.Power,
        value: isNil(powerSensorKw) ? 'N/A' : powerSensorKw,
        unit: 'kW',
      },
    ];

    const currentAsset = stationStore.assetsDevices?.filter(device => device.deviceId === deviceId)[0];

    const hasNoMatchingSpec = deviceModelId === DUMMY_MODEL_IDS.booster && renderedOnMap;
    const borderInfo = getBorderInfo(isSelected, inMaintenance, false, hasNoMatchingSpec, token);

    return (
      <Container
        width={width}
        height={height}
        $mapView={engine ? -1 : 1}
        className="booster-node-widget"
        $textColor={borderInfo?.textColor}
      >
        {borderInfo ? (
          <BoosterMaintenanceWidget
            width={width}
            height={height}
            fillColor={borderInfo.fillColor}
            borderColor={borderInfo.borderColor}
            className="background"
          />
        ) : (
          <BoosterWidget width={width} height={height} color={widgetFillColor} className="background" />
        )}

        {borderInfo && <div className="title">{t(borderInfo.headerText)}</div>}

        <header>
          {!!engine && (
            <div className="min0">
              <div className="name ellipsis">{name}</div>
              <div className="model ellipsis">
                {modelDisplayName} {brand}
              </div>
            </div>
          )}
          <ConnectivityIcon
            messages={messages}
            controlMode={controlMode}
            connectivity={connectivity}
            isCompressor={true}
            helixConnectivityStatus={currentAsset?.helixConnectivityStatusC}
            className={'control-icon'}
            width={renderedOnMap ? '24px' : '32px'}
            height={renderedOnMap ? '24px' : '32px'}
          />
        </header>

        {!!engine && (
          <>
            <div className="node-body">
              <section>
                <div className="metrics-list">
                  {mapViewMetrics.map(
                    metric =>
                      metric.value != null && (
                        <div className="metrics-list-item" key={metric.name}>
                          <span
                            className={`metrics-list-item-value ${isValueHighlighted(metric.name) && 'highlighted'}`}
                          >
                            {tcvalue(tvalue(metric.value, metric.unit), connectivity)}
                          </span>
                          <span className="metrics-list-item-label">
                            {tcunit(t(metric.unit), tvalue(metric.value, metric.unit), connectivity)}
                          </span>
                        </div>
                      ),
                  )}
                </div>
              </section>
              <div className="right-cell">
                <AlertsWidget alerts={node?.alerts} />
              </div>
            </div>
            <Ports node={node} engine={engine as DiagramEngine} width={width} height={height} />
          </>
        )}
      </Container>
    );
  },
);
